/**
 * Adobe Tracking Utilities
 *
 * Shared helpers for Adobe tracking.
 */

import { User } from 'library/models/User';
import { selectUser } from 'library/redux/application/Selectors';
import {
    errors_AuthenticationUrl,
    errors_AuthorizationUrl,
    notFoundUrl
} from 'main/routes/urls/RouteUrls';
import { store } from 'store/configureStore';

// ============================================================================
// Constants
// ============================================================================

export const ADOBE_TRACKING_ENABLED = import.meta.env.VITE_ADOBE_TRACKING_ENABLED === 'true';

export const ADOBE_TRACKING_URL = import.meta.env.VITE_ADOBE_TRACKING_URL;

const ADOBE_TRACKING_DEBUG = import.meta.env.VITE_ADOBE_TRACKING_DEBUG === 'true';

// ============================================================================
// Window Declaration
// ============================================================================

declare global {
    interface Window {
        adobeDataLayer?: Array<Record<string, unknown>>;
    }
}

if (typeof window !== 'undefined' && !window.adobeDataLayer) {
    window.adobeDataLayer = [];
}

// ============================================================================
// Logger
// ============================================================================

export const logDebug = (message: string, data?: unknown): void => {
    if (ADOBE_TRACKING_DEBUG) {
        console.log(`[AdobeTracking] ${message}`, data ?? '');
    }
};

export const logError = (message: string, error?: unknown): void => {
    if (ADOBE_TRACKING_DEBUG) {
        console.error(`[AdobeTracking] ${message}`, error ?? '');
    }
};

// ============================================================================
// User Utils
// ============================================================================

export const waitForUserAsync = (timeoutMs = 5000): Promise<User | undefined> => {
    return new Promise((resolve) => {
        // Check if user is available
        try {
            const state = store.getState();
            const user = selectUser(state);
            if (user?.userId) {
                logDebug('User available');
                resolve(user);
                return;
            }
        } catch (e) {
            logDebug('Could not get initial user from store:', e);
        }

        // Set up timeout
        const timeoutId = setTimeout(() => {
            logDebug('Timeout waiting for user data');
            unsubscribe();
            resolve(undefined);
        }, timeoutMs);

        // Subscribe to store changes
        const unsubscribe = store.subscribe(() => {
            try {
                const state = store.getState();
                const user = selectUser(state);
                if (user?.userId) {
                    logDebug('User data now available');
                    clearTimeout(timeoutId);
                    unsubscribe();
                    resolve(user);
                }
            } catch (e) {
                logDebug('Error checking user in subscription:', e);
            }
        });
    });
};

// ============================================================================
// Path Utils
// ============================================================================

const removeTrailingSlashes = (str: string): string => {
    while (str.endsWith('/')) {
        str = str.slice(0, -1);
    }
    return str;
};

const removeLeadingSlashes = (str: string): string => {
    while (str.startsWith('/')) {
        str = str.slice(1);
    }
    return str;
};

export const getBasePath = (): string => {
    const pcHost = import.meta.env.VITE_PC_HOST;
    if (pcHost && typeof pcHost === 'string') {
        try {
            const url = new URL(pcHost);
            return removeTrailingSlashes(url.pathname);
        } catch {
            return removeTrailingSlashes(pcHost);
        }
    }
    const baseUrl = import.meta.env.BASE_URL || '/';
    const basePath = removeTrailingSlashes(baseUrl);
    return basePath;
};

export const getCleanPath = (pathname: string): string => {
    const basePath = getBasePath();
    const lowerPathname = pathname.toLowerCase();
    const lowerBasePath = basePath.toLowerCase();
    if (lowerPathname.startsWith(lowerBasePath)) {
        return removeLeadingSlashes(pathname.slice(basePath.length));
    }
    const cleanPath = removeLeadingSlashes(pathname);
    logDebug(`getCleanPath: basePath='${basePath}', pathname='${pathname}', cleanPath='${cleanPath}'`);
    return cleanPath;
};

export const getSiteSection = (pathname: string): string => {
    const parts = pathname.split('/').filter(Boolean);
    const section = parts.length > 0 ? parts[0] : 'home';
    logDebug(`getSiteSection('${pathname}') = '${section}'`);
    return section;
};

export const isErrorPath = (pathname: string): boolean => {
    const lowerPath = removeLeadingSlashes(pathname.toLowerCase());
    const ERROR_PATH_PATTERNS = [
        errors_AuthenticationUrl,
        errors_AuthorizationUrl,
        notFoundUrl,
    ];
    const isError = ERROR_PATH_PATTERNS.some((pattern) => lowerPath.includes(removeLeadingSlashes(pattern.toLowerCase())));
    logDebug(`isErrorPath('${pathname}') = ${isError}`);
    return isError;
};

export const isHomePage = (pathname: string): boolean => {
    const cleanPath = removeLeadingSlashes(pathname);
    const isHome = cleanPath === '' || cleanPath === 'home';
    logDebug(`isHomePage('${pathname}') = ${isHome}`);
    return isHome;
};

export const getViewName = (pathname: string): string => {
    const ID_INT_REGEX = /^\d+$/;
    const ID_SHORT_GUID_REGEX = /^[a-z0-9_-]{22}$/i;
    const parts = pathname.toLowerCase().split('/');
    const normalized = parts.map((part) => {
        if (!part) return part;
        if (ID_INT_REGEX.test(part)) return '{id}';
        if (ID_SHORT_GUID_REGEX.test(part)) return '{id}';
        return part;
    });
    const result = removeLeadingSlashes(normalized.join('/'));
    return result || 'home';
};

// ============================================================================
// Data Layer
// ============================================================================

let isDataLayerInitialized = false;
let dataLayerInitResolvers: Array<() => void> = [];

/**
 * Mark the Adobe Data Layer as initialized.
 */
export const setDataLayerInitialized = (): void => {
    isDataLayerInitialized = true;
    logDebug('Adobe Data Layer marked as initialized');
    // Resolve all waiting promises
    dataLayerInitResolvers.forEach((resolve) => resolve());
    dataLayerInitResolvers = [];
};

/**
 * Reset data layer initialization state (for testing purposes).
 */
export const resetDataLayerState = (): void => {
    isDataLayerInitialized = false;
    dataLayerInitResolvers = [];
};

/**
 * Wait for Adobe Data Layer to be initialized.
 */
const waitForDataLayerInit = (timeoutMs = 10000): Promise<boolean> => {
    if (isDataLayerInitialized) {
        return Promise.resolve(true);
    }
    return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
            logDebug('Waiting for data layer initialization');
            resolve(false);
        }, timeoutMs);

        dataLayerInitResolvers.push(() => {
            clearTimeout(timeoutId);
            resolve(true);
        });
    });
};

/**
 * Check if Adobe Data Layer is properly initialized and functional.
 * Adobe script adds 'version' property when it initializes the dataLayer.
 */
const isDataLayerValid = (): boolean => {
    const dataLayer = window.adobeDataLayer as (Array<unknown> & { version?: string }) | undefined;

    if (!dataLayer || !Array.isArray(dataLayer)) {
        logError('adobeDataLayer not found or invalid');
        return false;
    }
    if (!dataLayer.version) {
        logError('adobeDataLayer.version not set - Adobe script not loaded');
        return false;
    }
    return true;
};

/**
 * Push payload to Adobe Data Layer
 */
export const pushToAdobeDataLayer = async (payload: Record<string, unknown>): Promise<void> => {
    if (!ADOBE_TRACKING_ENABLED) return;
    if (!ADOBE_TRACKING_URL) return;

    if (!(await waitForDataLayerInit())) {
        logError('Data layer initialization timed out');
        return;
    }

    if (!isDataLayerValid()) return;

    try {
        const length = window.adobeDataLayer!.length;
        window.adobeDataLayer!.push(payload);
        logDebug(`Payload pushed (${length} → ${window.adobeDataLayer!.length}):`, payload);
    } catch (error) {
        logError('Error pushing to adobeDataLayer:', error);
    }
};
